import { useState, useCallback } from 'react';
import { ActionCreatorWithPayload, AsyncThunk } from '@reduxjs/toolkit';
import { useAppDispatch } from 'hooks';
import { OnSuccessV2, OnFailure, FilterType } from 'types';
import { useSelector } from 'react-redux';
import { selectFilterReducer } from 'FiltersModule/selectors';
import { AxiosResponse, AxiosError } from 'axios';

type StateId = string;

type StateHash = Record<
  StateId,
  {
    isRequesting: boolean;
    error: any;
    data: any;
  }
>;

type FormatLocalDataArgs<UnformattedData, Error> = {
  error: AxiosError<Error>;
  stateData: UnformattedData;
  isRequesting: boolean;
  filter: FilterType;
};

const emptyObj = {};

const useLocalDispatch = () => {
  const [stateById, setStateHash] = useState<StateHash>(emptyObj);
  const filtersReducer = useSelector(selectFilterReducer);

  const dispatch = useAppDispatch();

  const selectLocalData = useCallback(
    <
      FormattedData,
      StateData = Record<string, unknown>,
      FilterExtraArgs = Record<string, unknown>,
      Error = null,
    >({
      stateId,
      filterId,
      formatData,
    }: {
      stateId: StateId;
      filterId?: number;
      formatData?: ({
        error,
        stateData,
        isRequesting,
        filter,
      }: FormatLocalDataArgs<StateData, Error>) => FormattedData;
    }) => {
      const { error = null, data, isRequesting = false } = stateById[stateId] || emptyObj;
      const filter: FilterType & FilterExtraArgs = (
        filterId
          ? filtersReducer[filterId] || ({} as FilterType & FilterExtraArgs)
          : ({} as FilterType & FilterExtraArgs)
      ) as FilterType & FilterExtraArgs;

      const formattedData: FormattedData = formatData
        ? formatData({ stateData: data, filter, error, isRequesting })
        : data;

      return {
        error,
        data: formattedData,
        isRequesting,
        filter,
      };
    },
    [stateById, filtersReducer],
  );

  const dispatchLocal = useCallback(
    <StateData, ThunkActionParams, SuccessResponse = any, Returned = any>(
      action: AsyncThunk<Returned, ThunkActionParams, Record<string, never>>,
      {
        params,
        initial = true,
        stateId,
        formatSuccessResponse, // Think of this as hitting a local reducer
        formatFailureResponse, // Format the response error before storing in state, mostly unnecessary
        onSuccessCallback, // To perform side effect calls once call is successful
        onFailureCallback,
        shouldUseMeta = false,
      }: {
        params: ThunkActionParams;
        stateId: string;
        initial?: boolean;
        // If true, put onSuccess and onFailure into meta object of the payload
        shouldUseMeta?: boolean;
        formatSuccessResponse?: ({
          response,
          prevData,
        }: {
          response: AxiosResponse<SuccessResponse>;
          prevData: StateData;
        }) => StateData;
        formatFailureResponse?: ({
          error,
          prevData,
        }: {
          error: AxiosError;
          prevData: StateData;
        }) => any;
        onSuccessCallback?: OnSuccessV2<SuccessResponse>;
        onFailureCallback?: OnFailure;
      },
    ) => {
      if (initial) {
        setStateHash((prev) => ({
          ...prev,
          [stateId]: {
            ...prev[stateId],
            isRequesting: true,
            error: null,
          },
        }));
      }

      const onSuccessToUse = ({ response }: { response: AxiosResponse<SuccessResponse> }) => {
        setStateHash((prev) => {
          const prevData = prev[stateId]?.data;
          const formattedSuccessData = formatSuccessResponse
            ? formatSuccessResponse({ response, prevData })
            : response.data;

          return {
            ...prev,
            [stateId]: {
              isRequesting: false,
              error: null,
              data: formattedSuccessData,
            },
          };
        });

        onSuccessCallback?.({ response });
      };

      const onFailureToUse = ({ error }: { error: AxiosError }) => {
        setStateHash((prev) => {
          const prevData = prev[stateId]?.data;
          const formattedFailureData: StateData = formatFailureResponse
            ? formatFailureResponse({ prevData, error })
            : prevData;

          return {
            ...prev,
            [stateId]: {
              data: formattedFailureData,
              isRequesting: false,
              error,
            },
          };
        });

        onFailureCallback?.({ error });
      };

      const metaParams = shouldUseMeta
        ? {
            meta: {
              onSuccess: onSuccessToUse,
              onFailure: onFailureToUse,
            },
          }
        : { onSuccess: onSuccessToUse, onFailure: onFailureToUse };

      void dispatch(
        // @ts-expect-error - Complex conditional type that TypeScript can't infer properly
        action({
          ...params,
          ...metaParams,
        }),
      );
    },
    [dispatch],
  );

  return {
    dispatchLocal,
    selectLocalData,
  };
};

export default useLocalDispatch;
